import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { QuestionTypeStep } from './steps/QuestionTypeStep';
import { SportLeagueStep } from './steps/SportLeagueStep';
import { FixtureStep } from './steps/FixtureStep';
import { TeamPlayerStep } from './steps/TeamPlayerStep';
import { TeamSelectionSubstep } from './steps/TeamSelectionSubstep';
import { PlayerOverUnderStep } from './steps/PlayerOverUnderStep';
import { StatTypeStep } from './steps/StatTypeStep';
import { MainMarketStep } from './steps/MainMarketStep';
import { ReviewStep } from './steps/ReviewStep';
import { BatchResultsPanel } from './steps/BatchResultsPanel';
import { Info } from 'lucide-react';
import {
  useCreateFeedQuestionMutation,
  useGetFeedLeaguesQuery,
  useGetFeedSportsQuery,
} from '@/api/feed';
import type { CreateFeedQuestionRequest } from '@/api/feed/createFeedQuestion';
import { MARGIN_CONSTRAINTS, MIN_TEAMS } from '@/constants/question-coach';
import { getScoreStatTypeForSport } from '@/constants/sport-stat-mapping';
import { useNavigate } from '@tanstack/react-router';
import { isIndividualSport } from '@low6-pickem/sports-config';

export type QuestionType =
  | 'player_selection'
  | 'team_selection'
  | 'player_over_under'
  | 'team_over_under'
  // Main markets (DKPLAT-500)
  | 'moneyline'
  | 'spread'
  | 'total';

/**
 * Check if a question type is team-based
 */
function isTeamQuestion(questionType?: QuestionType): boolean {
  return questionType === 'team_selection' || questionType === 'team_over_under';
}

/** Main markets graded off the final score (Moneyline / Spread / Total). */
function isMainMarket(questionType?: QuestionType): boolean {
  return questionType === 'moneyline' || questionType === 'spread' || questionType === 'total';
}

/** A valid half-point line (positive, ends in .5) — enforces no-push (DKPLAT-500 D2). */
function isHalfPointLine(margin?: number): boolean {
  return margin !== undefined && margin > 0 && margin % 1 === 0.5;
}

/**
 * Batch-capable question types (DKPLAT-501): these support selecting multiple
 * fixtures and generate one question per fixture (x team/player, where
 * applicable) instead of a single question. player_over_under (Phase 2) and
 * player_selection (Phase 3) are excluded for individual sports (PGA) — that
 * flow uses tournament rounds, not team fixtures, and keeps the legacy
 * single-fixture path below unchanged (team_selection has no PGA flow at all).
 */
export function isBatchQuestionType(questionType?: QuestionType, sportId?: string): boolean {
  if (
    questionType === 'team_over_under' ||
    questionType === 'total' ||
    questionType === 'spread' ||
    questionType === 'moneyline' ||
    questionType === 'team_selection'
  ) {
    return true;
  }
  if (questionType === 'player_over_under' || questionType === 'player_selection') {
    return !sportId || !isIndividualSport(sportId);
  }
  return false;
}

/**
 * One selected fixture in the batch-creation flow (DKPLAT-501). `thresholds` is
 * keyed by team ID for team-scoped markets (Spread favourite, Team Over/Under),
 * by player ID for Player Over/Under, and by the fixture ID itself for
 * match-level markets that have no team dimension (Total). Selection types
 * (player_selection/team_selection, Phase 3) carry no thresholds at all.
 */
export interface FixtureSelection {
  fixtureId: string;
  fixtureDisplay: string;
  fixtureTeamIds: string[];
  selectedTeamIds: string[];
  thresholds: Record<string, number | undefined>;
  /** Player Over/Under & Player Selection only: which team's roster is currently viewed (UI state). */
  selectedTeamIdForPlayer?: string;
  /** Player Over/Under & Player Selection only: players selected — may span both teams. */
  playerIds?: string[];
  playerNames?: string[];
}

export interface CoachData {
  questionType?: QuestionType;
  sportId?: string;
  sportCode?: string; // Sport code for stat type mapping (e.g., 'nfl', 'nba')
  sportName?: string;
  leagueId?: string;
  leagueName?: string;
  // Legacy single-fixture fields — still used by player_selection, team_selection
  // and player_over_under. Batch-capable types (see isBatchQuestionType) use
  // `fixtures` below instead.
  fixtureId?: string;
  fixtureDisplay?: string;
  fixtureTeamIds?: string[]; // Team IDs from the selected fixture(s)
  selectedTeamIdForPlayer?: string; // For player_over_under: team selected before player selection
  playerIds?: string[];
  playerNames?: string[];
  teamIds?: string[];
  teamNames?: string[];
  statTypeCode?: string;
  statTypeName?: string;
  margin?: number;
  /** Batch creation (DKPLAT-501 Phase 1): one entry per selected fixture. */
  fixtures?: FixtureSelection[];
}

/** A single planned question in the flattened (fixture x team/player) batch plan. */
export interface BatchPlannedQuestion {
  /** Unique per planned question: fixtureId, or `${fixtureId}:${teamId|playerId}` when scoped. */
  key: string;
  fixtureId: string;
  fixtureDisplay: string;
  teamId?: string;
  /** Player Over/Under only (Phase 2) — carried alongside the ID so review/results
   *  screens can show a name without a separate player-lookup fetch. */
  playerId?: string;
  playerName?: string;
  /** team_selection only (Phase 3): both team IDs included in this ONE question
   *  (selection questions carry multiple options per question, unlike Spread/
   *  Team Over/Under which create one question per option). */
  teamIds?: string[];
  /** player_selection only (Phase 3): all player IDs/names in this ONE question. */
  playerIds?: string[];
  playerNames?: string[];
  margin?: number;
  payload: Omit<CreateFeedQuestionRequest, 'pickemId'>;
}

export interface BatchItemResult extends BatchPlannedQuestion {
  status: 'pending' | 'success' | 'error';
  errorMessage?: string;
}

/**
 * Flattens the per-fixture (x team) selections into one create-question payload
 * per planned question, mirroring exactly what the single-question flow would
 * have sent for that fixture/team (DKPLAT-501 Phase 1).
 */
function buildBatchPlan(coachData: CoachData): BatchPlannedQuestion[] {
  const { questionType, leagueId, statTypeCode, fixtures, sportId } = coachData;
  if (
    !questionType ||
    !isBatchQuestionType(questionType, sportId) ||
    !statTypeCode ||
    !fixtures?.length
  ) {
    return [];
  }

  const items: BatchPlannedQuestion[] = [];

  for (const fixture of fixtures) {
    if (questionType === 'moneyline') {
      items.push({
        key: fixture.fixtureId,
        fixtureId: fixture.fixtureId,
        fixtureDisplay: fixture.fixtureDisplay,
        payload: {
          questionType,
          fixtureId: fixture.fixtureId,
          leagueId,
          statTypeCode,
          teamIds: fixture.fixtureTeamIds,
        },
      });
    } else if (questionType === 'total') {
      // Match-level market — no team dimension. Threshold keyed by fixtureId.
      const margin = fixture.thresholds[fixture.fixtureId];
      items.push({
        key: fixture.fixtureId,
        fixtureId: fixture.fixtureId,
        fixtureDisplay: fixture.fixtureDisplay,
        margin,
        payload: {
          questionType,
          fixtureId: fixture.fixtureId,
          leagueId,
          statTypeCode,
          margin,
        },
      });
    } else if (questionType === 'spread') {
      const teamId = fixture.selectedTeamIds[0];
      if (!teamId) continue;
      const margin = fixture.thresholds[teamId];
      items.push({
        key: `${fixture.fixtureId}:${teamId}`,
        fixtureId: fixture.fixtureId,
        fixtureDisplay: fixture.fixtureDisplay,
        teamId,
        margin,
        payload: {
          questionType,
          fixtureId: fixture.fixtureId,
          leagueId,
          statTypeCode,
          teamIds: [teamId],
          margin,
        },
      });
    } else if (questionType === 'team_over_under') {
      for (const teamId of fixture.selectedTeamIds) {
        const margin = fixture.thresholds[teamId];
        items.push({
          key: `${fixture.fixtureId}:${teamId}`,
          fixtureId: fixture.fixtureId,
          fixtureDisplay: fixture.fixtureDisplay,
          teamId,
          margin,
          payload: {
            questionType,
            fixtureId: fixture.fixtureId,
            leagueId,
            statTypeCode,
            teamIds: [teamId],
            margin,
          },
        });
      }
    } else if (questionType === 'player_over_under') {
      // Mirrors the legacy single-flow payload exactly: one player per question,
      // no teamIds sent (the team is only used client-side to filter the roster).
      const playerIds = fixture.playerIds ?? [];
      const playerNames = fixture.playerNames ?? [];
      playerIds.forEach((playerId, index) => {
        const margin = fixture.thresholds[playerId];
        items.push({
          key: `${fixture.fixtureId}:${playerId}`,
          fixtureId: fixture.fixtureId,
          fixtureDisplay: fixture.fixtureDisplay,
          playerId,
          playerName: playerNames[index],
          margin,
          payload: {
            questionType,
            fixtureId: fixture.fixtureId,
            leagueId,
            statTypeCode,
            playerIds: [playerId],
            margin,
          },
        });
      });
    } else if (questionType === 'team_selection') {
      // Selection question — ONE question per fixture with BOTH teams as options,
      // mirroring the legacy flow (a fixture always has exactly 2 teams, and
      // team_selection requires >= MIN_TEAMS.SELECTION, so both are always used).
      const teamIds = fixture.fixtureTeamIds;
      items.push({
        key: fixture.fixtureId,
        fixtureId: fixture.fixtureId,
        fixtureDisplay: fixture.fixtureDisplay,
        teamIds,
        payload: {
          questionType,
          fixtureId: fixture.fixtureId,
          leagueId,
          statTypeCode,
          teamIds,
        },
      });
    } else if (questionType === 'player_selection') {
      // Selection question — ONE question per fixture with ALL selected players
      // (>= 2, may span both teams) as options, mirroring the legacy payload
      // (playerIds only, no teamIds, no margin).
      const playerIds = fixture.playerIds ?? [];
      const playerNames = fixture.playerNames ?? [];
      items.push({
        key: fixture.fixtureId,
        fixtureId: fixture.fixtureId,
        fixtureDisplay: fixture.fixtureDisplay,
        playerIds,
        playerNames,
        payload: {
          questionType,
          fixtureId: fixture.fixtureId,
          leagueId,
          statTypeCode,
          playerIds,
        },
      });
    }
  }

  return items;
}

interface QuestionCoachModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pickemId: string;
  restrictedLeagueId?: string;
}

const STEPS = [
  { id: 1, title: 'Question Type', description: 'Choose the type of question' },
  { id: 2, title: 'Sport & League', description: 'Select sport and league' },
  { id: 3, title: 'Fixture', description: 'Choose the game/fixture' },
  { id: 4, title: 'Teams/Players', description: 'Select options' },
  { id: 5, title: 'Stat Type', description: 'Choose statistic' },
  { id: 6, title: 'Review', description: 'Review and create' },
];

export function QuestionCoachModal({
  open,
  onOpenChange,
  pickemId,
  restrictedLeagueId,
}: QuestionCoachModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [coachData, setCoachData] = useState<CoachData>({});
  const navigate = useNavigate();
  const createMutation = useCreateFeedQuestionMutation();

  // Fetch all leagues and sports to populate restricted league data - only when modal is open
  const { data: allLeagues } = useGetFeedLeaguesQuery(undefined, { enabled: open });
  const { data: sports } = useGetFeedSportsQuery({ enabled: open });

  const updateCoachData = (data: Partial<CoachData>) => {
    setCoachData(prev => ({ ...prev, ...data }));
  };

  // Set league and sport data when modal opens with restrictedLeagueId
  useEffect(() => {
    if (open && restrictedLeagueId && allLeagues && sports) {
      // Find the league matching restrictedLeagueId
      const league = allLeagues.find(l => l.externalId === restrictedLeagueId);

      if (league) {
        // Find the sport matching the league's sportId
        const sport = sports.find(
          s => s.code === league.sportId || s.externalId === league.sportId
        );

        setCoachData(prev => ({
          ...prev,
          leagueId: league.externalId,
          leagueName: league.name,
          sportId: league.sportId,
          sportCode: league.code || league.sportId,
          sportName: sport?.name || league.sportId,
        }));
      }
    }
  }, [open, restrictedLeagueId, allLeagues, sports]);

  // Main markets (DKPLAT-500): grade off the final score, so there is no stat to pick.
  // Auto-set statTypeCode to the sport's score stat (drives wording — goals/points/runs).
  // Moneyline's team selection is handled per-fixture at selection time (see
  // toggleFixtureSelection) rather than here, now that it batches across fixtures.
  useEffect(() => {
    if (!isMainMarket(coachData.questionType)) return;
    const sportCode = coachData.sportCode || coachData.sportId;
    if (sportCode && !coachData.statTypeCode) {
      const statTypeCode = getScoreStatTypeForSport(sportCode);
      setCoachData(prev => ({
        ...prev,
        statTypeCode,
        statTypeName: statTypeCode.charAt(0).toUpperCase() + statTypeCode.slice(1),
      }));
    }
  }, [coachData.questionType, coachData.sportCode, coachData.sportId, coachData.statTypeCode]);

  // Batch creation results (DKPLAT-501 Phase 1): populated once handleCreate runs for
  // a batch-capable question type. The modal stays open so results (with per-row
  // retry) can be shown instead of navigating away.
  const [batchResults, setBatchResults] = useState<BatchItemResult[] | null>(null);
  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);

  const batchPlan = useMemo(() => buildBatchPlan(coachData), [coachData]);

  const toggleFixtureSelection = (fixtureId: string, fixtureDisplay: string, teamIds: string[]) => {
    setCoachData(prev => {
      const existing = prev.fixtures ?? [];
      if (existing.some(f => f.fixtureId === fixtureId)) {
        return { ...prev, fixtures: existing.filter(f => f.fixtureId !== fixtureId) };
      }
      const newFixture: FixtureSelection = {
        fixtureId,
        fixtureDisplay,
        fixtureTeamIds: teamIds,
        // Moneyline and team_selection auto-include both teams (locked, non-editable
        // in step 4 — see toggleFixtureSelection callers/buildBatchPlan).
        selectedTeamIds:
          prev.questionType === 'moneyline' || prev.questionType === 'team_selection'
            ? teamIds
            : [],
        thresholds: {},
        selectedTeamIdForPlayer: undefined,
        playerIds: [],
        playerNames: [],
      };
      return { ...prev, fixtures: [...existing, newFixture] };
    });
  };

  const updateFixture = (fixtureId: string, patch: Partial<FixtureSelection>) => {
    setCoachData(prev => ({
      ...prev,
      fixtures: (prev.fixtures ?? []).map(f =>
        f.fixtureId === fixtureId ? { ...f, ...patch } : f
      ),
    }));
  };

  const toggleFixtureTeam = (fixtureId: string, teamId: string) => {
    setCoachData(prev => ({
      ...prev,
      fixtures: (prev.fixtures ?? []).map(f => {
        if (f.fixtureId !== fixtureId) return f;
        const alreadySelected = f.selectedTeamIds.includes(teamId);
        return {
          ...f,
          selectedTeamIds: alreadySelected
            ? f.selectedTeamIds.filter(id => id !== teamId)
            : [...f.selectedTeamIds, teamId],
        };
      }),
    }));
    // Default the stat type the first time a team is picked, mirroring the legacy
    // single-fixture team_over_under behaviour.
    if (!coachData.statTypeCode) {
      const sportCode = coachData.sportCode || coachData.sportId;
      if (sportCode) {
        const statTypeCode = getScoreStatTypeForSport(sportCode);
        updateCoachData({
          statTypeCode,
          statTypeName: statTypeCode.charAt(0).toUpperCase() + statTypeCode.slice(1),
        });
      }
    }
  };

  /**
   * Player Over/Under (Phase 2): switch which team's roster is shown for a fixture.
   * Only changes the viewed roster — player selections accumulate across both teams.
   */
  const selectFixturePlayerTeam = (fixtureId: string, teamId: string) => {
    updateFixture(fixtureId, { selectedTeamIdForPlayer: teamId });
  };

  /** Player Over/Under (Phase 2): free multi-select of players within the chosen team. */
  const toggleFixturePlayer = (
    fixtureId: string,
    playerId: string,
    playerName: string,
    checked: boolean
  ) => {
    setCoachData(prev => ({
      ...prev,
      fixtures: (prev.fixtures ?? []).map(f => {
        if (f.fixtureId !== fixtureId) return f;
        const ids = f.playerIds ?? [];
        const names = f.playerNames ?? [];
        if (checked) {
          if (ids.includes(playerId)) return f;
          return { ...f, playerIds: [...ids, playerId], playerNames: [...names, playerName] };
        }
        const index = ids.indexOf(playerId);
        if (index === -1) return f;
        return {
          ...f,
          playerIds: ids.filter((_, i) => i !== index),
          playerNames: names.filter((_, i) => i !== index),
        };
      }),
    }));
    // No stat-type default here: the score stat (e.g. 'goals') is a TEAM stat and is
    // invalid for player questions — the user picks a player stat in step 5, as in
    // the legacy single-fixture flow.
  };

  const updateFixtureThreshold = (fixtureId: string, key: string, margin: number | undefined) => {
    setCoachData(prev => ({
      ...prev,
      fixtures: (prev.fixtures ?? []).map(f =>
        f.fixtureId === fixtureId ? { ...f, thresholds: { ...f.thresholds, [key]: margin } } : f
      ),
    }));
  };

  const currentStepData = STEPS[currentStep - 1];

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 1:
        return !!coachData.questionType;
      case 2:
        return !!coachData.sportId && !!coachData.leagueId;
      case 3:
        if (isBatchQuestionType(coachData.questionType, coachData.sportId)) {
          return (coachData.fixtures?.length || 0) >= 1;
        }
        return !!coachData.fixtureId;
      case 4:
        // Batch-capable types (DKPLAT-501): validate per selected fixture.
        if (isBatchQuestionType(coachData.questionType, coachData.sportId)) {
          const fixtures = coachData.fixtures ?? [];
          if (fixtures.length === 0) return false;
          if (coachData.questionType === 'spread') {
            return fixtures.every(f => f.selectedTeamIds.length === 1); // the favourite
          }
          if (coachData.questionType === 'team_over_under') {
            return fixtures.every(f => f.selectedTeamIds.length >= MIN_TEAMS.OVER_UNDER);
          }
          if (coachData.questionType === 'player_over_under') {
            // Per fixture: 1+ players, from either team (selections span both rosters).
            return fixtures.every(f => (f.playerIds?.length || 0) >= MIN_TEAMS.OVER_UNDER);
          }
          if (coachData.questionType === 'player_selection') {
            // Per fixture: 2+ players, from either team (selections span both rosters).
            return fixtures.every(f => (f.playerIds?.length || 0) >= MIN_TEAMS.SELECTION);
          }
          // moneyline & team_selection: both teams auto-included and locked.
          // total: match-level, no team.
          return true;
        }
        if (coachData.questionType === 'player_selection') {
          return (coachData.playerIds?.length || 0) >= 2; // Needs 2+ players
        }
        if (coachData.questionType === 'player_over_under') {
          // Two-phase validation for player_over_under:
          // Phase 1: Team selection - must have selected a team
          // Phase 2: Player selection - must have selected 1+ player from that team
          if (!coachData.selectedTeamIdForPlayer) {
            return false; // Phase 1: Need to select a team first
          }
          return (coachData.playerIds?.length || 0) >= MIN_TEAMS.OVER_UNDER; // Phase 2: Need 1 player
        }
        if (coachData.questionType === 'team_over_under') {
          return (coachData.teamIds?.length || 0) >= MIN_TEAMS.OVER_UNDER; // Needs only 1 team
        }
        return (coachData.teamIds?.length || 0) >= MIN_TEAMS.SELECTION; // team_selection needs 2 teams
      case 5:
        // Batch-capable types (DKPLAT-501 Phase 1): validate per selected fixture (x team).
        if (isBatchQuestionType(coachData.questionType, coachData.sportId)) {
          const fixtures = coachData.fixtures ?? [];
          if (fixtures.length === 0) return false;
          if (coachData.questionType === 'moneyline') return true;
          if (!coachData.statTypeCode) return false;
          if (
            coachData.questionType === 'team_selection' ||
            coachData.questionType === 'player_selection'
          ) {
            // Selection questions carry no threshold — step 4 already enforced the
            // per-fixture option counts, so a stat type is all that's left to check.
            return true;
          }
          if (coachData.questionType === 'spread') {
            return fixtures.every(f => {
              const teamId = f.selectedTeamIds[0];
              return !!teamId && isHalfPointLine(f.thresholds[teamId]);
            });
          }
          if (coachData.questionType === 'total') {
            return fixtures.every(f => isHalfPointLine(f.thresholds[f.fixtureId]));
          }
          if (coachData.questionType === 'player_over_under') {
            return fixtures.every(
              f =>
                (f.playerIds?.length || 0) > 0 &&
                (f.playerIds ?? []).every(playerId => {
                  const playerMargin = f.thresholds[playerId];
                  return playerMargin !== undefined && playerMargin >= MARGIN_CONSTRAINTS.MIN;
                })
            );
          }
          // team_over_under
          return fixtures.every(
            f =>
              f.selectedTeamIds.length > 0 &&
              f.selectedTeamIds.every(teamId => {
                const margin = f.thresholds[teamId];
                return margin !== undefined && margin >= MARGIN_CONSTRAINTS.MIN;
              })
          );
        }

        // Stat type is required for all question types
        if (!coachData.statTypeCode) return false;

        // Margin is required for over/under questions
        const isOverUnder =
          coachData.questionType === 'player_over_under' ||
          coachData.questionType === 'team_over_under';
        if (
          isOverUnder &&
          (coachData.margin === undefined || coachData.margin < MARGIN_CONSTRAINTS.MIN)
        ) {
          return false;
        }

        return true;
      case 6:
        return true;
      default:
        return false;
    }
  }, [currentStep, coachData]);

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Legacy single-fixture player_over_under (PGA/individual sports only — batch
      // mode's per-fixture team→players phase lives inside PlayerOverUnderStep's own
      // UI, not in wizard step navigation, so it must not hit these special cases).
      const isLegacyPlayerOverUnder =
        coachData.questionType === 'player_over_under' &&
        !isBatchQuestionType(coachData.questionType, coachData.sportId);

      // Special handling for player_over_under two-phase step 4
      if (currentStep === 4 && isLegacyPlayerOverUnder && coachData.selectedTeamIdForPlayer) {
        // In player selection phase, go back to team selection phase
        updateCoachData({
          selectedTeamIdForPlayer: undefined,
          playerIds: undefined,
          playerNames: undefined,
        });
        return; // Stay on step 4, just reset to team selection phase
      }

      if (currentStep === 5 && isLegacyPlayerOverUnder) {
        // Going back from step 5, clear player selection to show player phase again
        updateCoachData({
          playerIds: undefined,
          playerNames: undefined,
        });
        setCurrentStep(4);
        return;
      }

      setCurrentStep(currentStep - 1);
    }
  };

  /** Submits a single planned question and reports its outcome — never throws. */
  const submitBatchItem = async (item: BatchPlannedQuestion): Promise<BatchItemResult> => {
    try {
      await createMutation.mutateAsync({ pickemId, ...item.payload });
      return { ...item, status: 'success' };
    } catch (error) {
      return {
        ...item,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Failed to create question',
      };
    }
  };

  const handleCreate = async () => {
    // Batch creation (DKPLAT-501 Phase 1): submit sequentially (never Promise.all) so
    // the backend never sees concurrent creates for the same pickem, and so partial
    // failures can be retried individually. The modal stays open — the user reviews
    // the results and dismisses it themselves; we never navigate away here.
    if (isBatchQuestionType(coachData.questionType, coachData.sportId)) {
      if (batchPlan.length === 0) return;
      setIsBatchSubmitting(true);
      setBatchResults(batchPlan.map(item => ({ ...item, status: 'pending' })));
      for (const item of batchPlan) {
        const result = await submitBatchItem(item);
        setBatchResults(prev => (prev ?? []).map(r => (r.key === item.key ? result : r)));
      }
      setIsBatchSubmitting(false);
      return;
    }

    // Legacy single-question flow (player_selection, team_selection, player_over_under).
    if (!coachData.questionType || !coachData.fixtureId || !coachData.statTypeCode) {
      return;
    }

    try {
      await createMutation.mutateAsync({
        pickemId,
        questionType: coachData.questionType,
        fixtureId: coachData.fixtureId,
        leagueId: coachData.leagueId,
        statTypeCode: coachData.statTypeCode,
        playerIds: coachData.playerIds,
        teamIds: coachData.teamIds,
        margin: coachData.margin,
      });

      // Close modal and navigate back to pickem detail page (Questions tab)
      onOpenChange(false);
      navigate({
        to: '/dashboard/pickems/$id',
        params: { id: pickemId },
        search: { tenant_id: undefined, tab: 'questions' },
      });
    } catch (error) {
      console.error('Failed to create question:', error);
    }
  };

  /** Retries a single failed batch item — succeeded rows are left untouched. */
  const handleRetryBatchItem = async (key: string) => {
    const item = batchResults?.find(r => r.key === key);
    if (!item) return;
    setBatchResults(prev =>
      (prev ?? []).map(r =>
        r.key === key ? { ...r, status: 'pending', errorMessage: undefined } : r
      )
    );
    const result = await submitBatchItem(item);
    setBatchResults(prev => (prev ?? []).map(r => (r.key === key ? result : r)));
  };

  const handleRetryAllFailed = async () => {
    const failedKeys = (batchResults ?? []).filter(r => r.status === 'error').map(r => r.key);
    for (const key of failedKeys) {
      await handleRetryBatchItem(key);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setCoachData({});
    setBatchResults(null);
    setIsBatchSubmitting(false);
    onOpenChange(false);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: {
        // Filter out team-based types for individual sports (PGA)
        const isIndividual = coachData.sportId ? isIndividualSport(coachData.sportId) : false;
        const enabledTypes: QuestionType[] = isIndividual
          ? ['player_selection', 'player_over_under']
          : [
              'player_selection',
              'team_selection',
              'player_over_under',
              'team_over_under',
              // Main markets (DKPLAT-500) — team sports only.
              'moneyline',
              'spread',
              'total',
            ];
        return (
          <QuestionTypeStep
            selectedType={coachData.questionType}
            onSelect={questionType => {
              // Reset everything downstream of question type — switching between a
              // batch-capable type and a legacy single-fixture type mixes incompatible
              // shapes (fixtures vs fixtureId/teamIds), so start each type fresh.
              setCoachData({
                questionType,
                sportId: coachData.sportId,
                sportCode: coachData.sportCode,
                sportName: coachData.sportName,
                leagueId: coachData.leagueId,
                leagueName: coachData.leagueName,
              });
            }}
            enabledTypes={enabledTypes}
          />
        );
      }
      case 2:
        return (
          <SportLeagueStep
            restrictedLeagueId={restrictedLeagueId}
            selectedSportId={coachData.sportId}
            selectedLeagueId={coachData.leagueId}
            onSelectSport={(sportId, sportCode, sportName) => {
              updateCoachData({
                sportId,
                sportName,
                sportCode: sportCode, // sportId is the sport code (e.g., 'nba', 'nfl')
                leagueId: undefined,
                leagueName: undefined,
              });
            }}
            onSelectLeague={(leagueId, leagueName) => updateCoachData({ leagueId, leagueName })}
          />
        );
      case 3:
        // Batch creation (DKPLAT-501 Phase 1): multi-select fixtures.
        if (isBatchQuestionType(coachData.questionType, coachData.sportId)) {
          return (
            <FixtureStep
              leagueId={coachData.leagueId}
              sportId={coachData.sportId}
              multiSelect
              selectedFixtureIds={(coachData.fixtures ?? []).map(f => f.fixtureId)}
              onToggleFixture={toggleFixtureSelection}
            />
          );
        }
        return (
          <FixtureStep
            leagueId={coachData.leagueId}
            sportId={coachData.sportId}
            selectedFixtureId={coachData.fixtureId}
            onSelect={(fixtureId, fixtureDisplay, teamIds) =>
              updateCoachData({ fixtureId, fixtureDisplay, fixtureTeamIds: teamIds })
            }
          />
        );
      case 4: {
        // Batch creation (DKPLAT-501 Phase 1): one group per selected fixture.
        if (isBatchQuestionType(coachData.questionType, coachData.sportId)) {
          const fixtures = coachData.fixtures ?? [];
          if (fixtures.length === 0) {
            return (
              <Alert>
                <AlertDescription>Please select at least one fixture first.</AlertDescription>
              </Alert>
            );
          }
          if (coachData.questionType === 'total') {
            return (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Total is a match-level market (combined score) — no team selection needed for the{' '}
                  {fixtures.length} selected fixture{fixtures.length === 1 ? '' : 's'}. Continue to
                  set the lines.
                </AlertDescription>
              </Alert>
            );
          }
          if (coachData.questionType === 'moneyline') {
            return (
              <TeamSelectionSubstep
                leagueId={coachData.leagueId}
                mode="locked"
                fixtures={fixtures}
                title="Both teams are offered automatically"
                description="Home / Away (plus Draw for draw-capable sports) are included for every selected fixture. Continue to review."
              />
            );
          }
          if (coachData.questionType === 'spread') {
            return (
              <TeamSelectionSubstep
                leagueId={coachData.leagueId}
                fixtures={fixtures}
                mode="radio"
                title="Select the favourite team for each fixture"
                description="Pick the team giving the handicap (the favourite) in each fixture. The other team becomes the underdog."
                onSelectForFixture={(fixtureId, teamId) =>
                  updateFixture(fixtureId, { selectedTeamIds: teamId ? [teamId] : [] })
                }
              />
            );
          }
          if (coachData.questionType === 'player_over_under') {
            // Team-then-players two-phase picker, per fixture (Phase 2). Unlike the
            // legacy flow this allows multiple players per team — one question is
            // created per (fixture x player).
            return (
              <PlayerOverUnderStep
                leagueId={coachData.leagueId}
                fixtures={fixtures}
                onSelectTeamForFixture={selectFixturePlayerTeam}
                onTogglePlayerForFixture={toggleFixturePlayer}
              />
            );
          }
          if (coachData.questionType === 'team_selection') {
            // Selection question (Phase 3): a fixture only has 2 teams and the legacy
            // flow requires >= MIN_TEAMS.SELECTION, so both are always the options —
            // shown locked, like Moneyline. ONE question per fixture (not per team).
            return (
              <TeamSelectionSubstep
                leagueId={coachData.leagueId}
                mode="locked"
                fixtures={fixtures}
                title="Both teams are included automatically"
                description="A Team Selection question needs both teams from the fixture as its options. Continue to review."
              />
            );
          }
          if (coachData.questionType === 'player_selection') {
            // Selection question (Phase 3): reuses the Player Over/Under picker
            // (roster tabs, selections span both teams) but requires 2+ players and
            // creates ONE question per fixture with all selected players as options.
            return (
              <PlayerOverUnderStep
                leagueId={coachData.leagueId}
                fixtures={fixtures}
                minPlayers={MIN_TEAMS.SELECTION}
                title="Select players for each fixture"
                description="Choose 2 or more players from either team — one Player Selection question is created per fixture with all of them as options."
                onSelectTeamForFixture={selectFixturePlayerTeam}
                onTogglePlayerForFixture={toggleFixturePlayer}
              />
            );
          }
          // team_over_under: free multi-select per fixture.
          return (
            <TeamPlayerStep
              questionType={coachData.questionType}
              leagueId={coachData.leagueId}
              fixtures={fixtures}
              onToggleTeamForFixture={(fixtureId, teamId) => toggleFixtureTeam(fixtureId, teamId)}
            />
          );
        }

        // For player_over_under: Show team selection first, then player selection
        if (coachData.questionType === 'player_over_under' && !coachData.selectedTeamIdForPlayer) {
          // Phase 1: Team selection
          return (
            <TeamSelectionSubstep
              leagueId={coachData.leagueId}
              fixtureTeamIds={coachData.fixtureTeamIds}
              selectedTeamId={coachData.selectedTeamIdForPlayer}
              onSelect={teamId => {
                updateCoachData({
                  selectedTeamIdForPlayer: teamId,
                  // Clear any previous player selection when changing team
                  playerIds: teamId ? undefined : coachData.playerIds,
                  playerNames: teamId ? undefined : coachData.playerNames,
                });
              }}
            />
          );
        }

        // For all other question types (and player_over_under after team selected)
        return (
          <TeamPlayerStep
            questionType={coachData.questionType}
            leagueId={coachData.leagueId}
            fixtureId={coachData.fixtureId}
            fixtureTeamIds={coachData.fixtureTeamIds}
            selectedPlayerIds={coachData.playerIds}
            selectedTeamIds={coachData.teamIds}
            selectedTeamIdForPlayer={coachData.selectedTeamIdForPlayer} // Pass for filtering players
            onSelectPlayers={(playerIds, playerNames) =>
              updateCoachData({ playerIds, playerNames })
            }
            onSelectTeams={(teamIds, teamNames) => {
              const updates: Partial<CoachData> = { teamIds, teamNames };
              // Pre-select default stat type for team questions (user can change it in step 5)
              // Only set if not already set, to allow user to change it
              if (isTeamQuestion(coachData.questionType) && !coachData.statTypeCode) {
                const sportCode = coachData.sportCode || coachData.sportId;
                if (sportCode) {
                  const statTypeCode = getScoreStatTypeForSport(sportCode);
                  updates.statTypeCode = statTypeCode;
                  // Capitalize first letter for display (e.g., "points" -> "Points")
                  updates.statTypeName =
                    statTypeCode.charAt(0).toUpperCase() + statTypeCode.slice(1);
                }
              }
              updateCoachData(updates);
            }}
          />
        );
      }
      case 5: {
        // Batch creation (DKPLAT-501 Phase 1): stacked cards, one per selected fixture.
        if (isBatchQuestionType(coachData.questionType, coachData.sportId)) {
          const fixtures = coachData.fixtures ?? [];
          if (isMainMarket(coachData.questionType)) {
            return (
              <MainMarketStep
                questionType={coachData.questionType!}
                fixtures={fixtures}
                leagueId={coachData.leagueId}
                onThresholdChange={updateFixtureThreshold}
              />
            );
          }
          // team_over_under: shared stat type + per-fixture/team threshold cards.
          return (
            <StatTypeStep
              sportId={coachData.leagueId ?? coachData.sportId}
              leagueId={coachData.leagueId}
              questionType={coachData.questionType}
              selectedStatTypeCode={coachData.statTypeCode}
              fixtures={fixtures}
              onSelect={(statTypeCode, statTypeName) =>
                updateCoachData({ statTypeCode, statTypeName })
              }
              onThresholdChange={updateFixtureThreshold}
            />
          );
        }

        // Main markets (DKPLAT-500): no stat picker — Moneyline needs nothing, Spread/Total
        // take a half-point line. statTypeCode is auto-set to the score stat.
        if (isMainMarket(coachData.questionType)) {
          return (
            <MainMarketStep
              questionType={coachData.questionType!}
              favouriteTeamName={coachData.teamNames?.[0]}
              margin={coachData.margin}
              onMarginChange={margin => updateCoachData({ margin })}
            />
          );
        }
        return (
          <StatTypeStep
            sportId={coachData.leagueId ?? coachData.sportId}
            questionType={coachData.questionType}
            selectedStatTypeCode={coachData.statTypeCode}
            margin={coachData.margin}
            onSelect={(statTypeCode, statTypeName) =>
              updateCoachData({ statTypeCode, statTypeName })
            }
            onMarginChange={margin => updateCoachData({ margin })}
          />
        );
      }
      case 6:
        if (isBatchQuestionType(coachData.questionType, coachData.sportId)) {
          if (batchResults) {
            return (
              <BatchResultsPanel
                results={batchResults}
                leagueId={coachData.leagueId}
                onRetry={handleRetryBatchItem}
              />
            );
          }
          return <ReviewStep coachData={coachData} batchPlan={batchPlan} />;
        }
        return <ReviewStep coachData={coachData} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <TooltipProvider>
          <DialogHeader>
            <DialogTitle className="text-2xl">Question Coach</DialogTitle>
            <DialogDescription>
              Step {currentStep} of {STEPS.length}: {currentStepData.description}
            </DialogDescription>
          </DialogHeader>

          {/* Progress indicator */}
          <div className="flex gap-2 mb-4">
            {STEPS.map(step => (
              <div
                key={step.id}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  step.id === currentStep
                    ? 'bg-primary'
                    : step.id < currentStep
                      ? 'bg-primary/50'
                      : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Error display — batch mode shows per-row errors in the results panel instead. */}
          {createMutation.isError &&
            !isBatchQuestionType(coachData.questionType, coachData.sportId) && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : 'Failed to create question'}
                </AlertDescription>
              </Alert>
            )}

          {/* Step content */}
          <ErrorBoundary onReset={() => setCurrentStep(1)}>
            <div className="py-4">{renderStepContent()}</div>
          </ErrorBoundary>

          <DialogFooter className="flex justify-between">
            {batchResults ? (
              <>
                <div />
                <div className="flex gap-2">
                  {batchResults.some(r => r.status === 'error') && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" onClick={handleRetryAllFailed}>
                          Retry Failed
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Re-submits every failed row; leaves successes as-is
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <Button onClick={handleClose}>Done</Button>
                </div>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1 || isBatchSubmitting}
                >
                  Back
                </Button>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleClose} disabled={isBatchSubmitting}>
                    Cancel
                  </Button>

                  {currentStep < STEPS.length ? (
                    <Button onClick={handleNext} disabled={!canProceed}>
                      Next
                    </Button>
                  ) : (
                    <Button
                      onClick={handleCreate}
                      disabled={!canProceed || createMutation.isPending || isBatchSubmitting}
                      className="min-w-[120px]"
                    >
                      {createMutation.isPending || isBatchSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating...
                        </>
                      ) : isBatchQuestionType(coachData.questionType, coachData.sportId) ? (
                        `Create ${batchPlan.length} Question${batchPlan.length === 1 ? '' : 's'}`
                      ) : (
                        'Create Question'
                      )}
                    </Button>
                  )}
                </div>
              </>
            )}
          </DialogFooter>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
